/**
 * Which image a signature logo accepts, and where one can come from (#1451).
 *
 * The editor has three ways in — the toolbar's file picker, a paste, and a drop — and they must
 * agree. If pasting accepted something the picker refuses, the looser path would be the one people
 * use, and the limits below would describe nothing. So the decision lives here once and every
 * entry point asks it.
 *
 * Deliberately free of React, Lexical and the DOM: a `DataTransfer` is only read for its `files`,
 * which keeps this testable without a browser and keeps the rules visible in one place.
 */

/** PNG and JPEG only. Everything else is refused before it reaches the upload. */
export const SIGNATURE_LOGO_TYPES = ["image/png", "image/jpeg"] as const;

/** The same list for the file input's `accept`, so the picker cannot drift from the check. */
export const SIGNATURE_LOGO_ACCEPT = SIGNATURE_LOGO_TYPES.join(",");

/** 512 KB. A logo is a small image in an email footer, not an attachment. */
export const SIGNATURE_LOGO_MAX_BYTES = 512 * 1024;

export type SignatureLogoRejection = "type" | "size";

/** A `DataTransfer`, reduced to what this module reads — clipboard and drop carry the same shape. */
export interface SignatureTransfer {
  readonly files?: ArrayLike<File> | null;
}

/** Why this file cannot be a logo, or `null` when it can. */
export function signatureLogoRejection(file: File): SignatureLogoRejection | null {
  if (!(SIGNATURE_LOGO_TYPES as readonly string[]).includes(file.type)) return "type";
  if (file.size === 0 || file.size > SIGNATURE_LOGO_MAX_BYTES) return "size";
  return null;
}

/**
 * The image a paste or a drop is offering, if it is offering one at all.
 *
 * Any image type qualifies here, not only the accepted two, and that is the point: a pasted GIF is
 * somebody trying to insert a logo, so it has to reach the upload and be refused there with the
 * message the picker gives for the same file. Judging the type here instead would let it fall
 * through to the paste's text branch, which inserts markup and says nothing — a refusal the person
 * never sees.
 *
 * A file that is not an image at all — a PDF, a document — is not an attempt at a logo, so it is
 * left to the ordinary text handling.
 *
 * A browser copying an image puts both a file and an `<img>` in `text/html` on the clipboard. The
 * file wins, because that HTML points at a URL the signature policy strips: taking the HTML branch
 * would drop the image without a word.
 */
export function signatureImageFromTransfer(transfer: SignatureTransfer | null | undefined): File | null {
  const files = transfer?.files;
  if (!files) return null;
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    if (file && file.type.startsWith("image/")) return file;
  }
  return null;
}
